// Store.tsx - Enhanced store page with product filtering, cart functionality,
// featured products, and improved display of merchandise

import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';
import '../styles/Store.css';
import { useNavigate } from 'react-router-dom';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { useCart } from '../context/CartContext';
import toast, { Toaster } from 'react-hot-toast';
import { categoryOptions } from '../utils/options';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  category?: string;
  featured?: boolean;
  stock?: number;
  details?: string[];
  size?: string;
  color?: string;
}

function toTitleCase(str: string) {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const Store: React.FC = () => {
  // State for products and UI
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // State for product details modal
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const { cart, addToCart, removeFromCart, updateQuantity } = useCart();
  const [cartOpen, setCartOpen] = useState<boolean>(false);

  const navigate = useNavigate();

  // Fetch products from Firestore
  useEffect(() => {
    const productsRef = collection(db, 'products');
    const unsubscribe = onSnapshot(
      productsRef,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const productsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Product[];

        setProducts(productsData);
        setFilteredProducts(productsData);

        // Extract featured products
        const featured = productsData.filter((product) => product.featured);
        setFeaturedProducts(featured);

        // Extract unique categories
        const allCategories = productsData.map(
          (product) => product.category || 'uncategorized'
        );
        const uniqueCategories = ['all'];
        allCategories.forEach((category) => {
          if (category && !uniqueCategories.includes(category)) {
            uniqueCategories.push(category);
          }
        });

        setLoading(false);
      },
      (error) => {
        console.error('Error fetching products:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Filter products by category and search term
  useEffect(() => {
    let filtered = products;

    // Filter by category
    if (selectedCategory.toLowerCase() !== 'all') {
      filtered = filtered.filter(
        (product) => product.category === selectedCategory
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(term) ||
          product.description.toLowerCase().includes(term)
      );
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, searchTerm, products]);

  // Calculate cart total
  const cartTotal = cart.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );

  // Open product details modal
  const openProductDetails = (product: Product) => {
    setSelectedProduct(product);
  };

  // Close product details modal
  const closeProductDetails = () => {
    setSelectedProduct(null);
  };

  // Navigate to checkout
  const navigateToCheckout = () => {
    setCartOpen(false);
    navigate('/checkout', { state: { cart } });
  };

  const handleAddToCart = (product: Product, quantity: number) => {
    addToCart(product, quantity);
    closeProductDetails();
    toast.success(`${quantity} ${product.name} added to cart! 🛒`, {
      duration: 3000,
      position: 'bottom-right',
      style: {
        background: '#333',
        color: '#fff',
        padding: '16px',
        borderRadius: '8px',
      },
    });
  };

  return (
    <div className="store-container">
      <Toaster />
      {/* Store Header */}
      <div className="store-header">
        <h1 className="section-title">Sip'N'Security Merchandise</h1>
        <p className="store-description">
          Elevate your bourbon experience and showcase your love for
          cybersecurity with our premium merchandise. Each item is crafted with
          the same attention to detail that goes into every episode of our
          podcast.
        </p>
      </div>

      {/* Search and Filter Bar */}
      <div className="store-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="search-button">🔍</button>
        </div>

        <div className="category-filter">
          {categoryOptions.map((category) => (
            <button
              key={category}
              className={`category-btn ${
                selectedCategory === category ? 'active' : ''
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {toTitleCase(category)}
            </button>
          ))}
        </div>

        <div className="cart-button" onClick={() => setCartOpen(!cartOpen)}>
          <span className="cart-icon">🛒</span>
          <span className="cart-count">{cart.length}</span>
        </div>
      </div>

      {/* Featured Products - Carousel */}
      {featuredProducts.length > 0 &&
        selectedCategory === 'all' &&
        !searchTerm && (
          <div className="featured-products">
            <h2>Featured Products</h2>
            <Carousel
              additionalTransfrom={0}
              arrows
              autoPlay
              autoPlaySpeed={2000}
              centerMode={false}
              className="featured-carousel"
              containerClass="carousel-container"
              dotListClass=""
              draggable
              focusOnSelect={false}
              infinite
              itemClass="carousel-item-padding-40-px"
              keyBoardControl
              minimumTouchDrag={80}
              pauseOnHover
              renderButtonGroupOutside={false}
              renderDotsOutside={false}
              responsive={{
                superLargeDesktop: {
                  breakpoint: { max: 4000, min: 1200 },
                  items: 3,
                },
                desktop: { breakpoint: { max: 1200, min: 900 }, items: 2 },
                tablet: { breakpoint: { max: 900, min: 600 }, items: 1 },
                mobile: { breakpoint: { max: 600, min: 0 }, items: 1 },
              }}
              showDots={false}
              sliderClass=""
              slidesToSlide={1}
              swipeable
            >
              {featuredProducts.map((product) => (
                <div key={product.id} className="featured-product-card">
                  <div className="featured-badge">Featured</div>
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="featured-product-image"
                  />
                  <div className="featured-product-info">
                    <h3>{product.name}</h3>
                    <p className="featured-price">
                      ${product.price.toFixed(2)}
                    </p>
                    <div className="featured-actions">
                      <button
                        className="view-details-btn"
                        onClick={() => openProductDetails(product)}
                      >
                        View Details
                      </button>
                      <button
                        className="add-to-cart-btn"
                        onClick={() => handleAddToCart(product, 1)}
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        )}

      {/* Main Product Grid */}
      <div className="product-section">
        <h2>
          {selectedCategory !== 'all'
            ? `${
                selectedCategory.charAt(0).toUpperCase() +
                selectedCategory.slice(1)
              } Products`
            : 'All Products'}
          {searchTerm && ` matching "${searchTerm}"`}
        </h2>

        {loading ? (
          <div className="loading-spinner"></div>
        ) : filteredProducts.length > 0 ? (
          <div className="product-grid">
            {filteredProducts.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-image-container">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="product-image"
                    onClick={() => openProductDetails(product)}
                  />
                  {product.stock && product.stock < 5 && (
                    <div className="low-stock-badge">
                      {product.stock === 0
                        ? 'Sold Out'
                        : `Only ${product.stock} left!`}
                    </div>
                  )}
                </div>
                <div className="product-info">
                  <h3 onClick={() => openProductDetails(product)}>
                    {product.name}
                  </h3>
                  <p className="product-description">
                    {product.description.length > 80
                      ? `${product.description.substring(0, 80)}...`
                      : product.description}
                  </p>
                  <div className="product-footer">
                    <p className="price">${product.price.toFixed(2)}</p>
                    <div className="product-actions">
                      <button
                        className="view-details-btn"
                        onClick={() => openProductDetails(product)}
                      >
                        View Details
                      </button>
                      <button
                        className="add-to-cart-btn small"
                        onClick={() => handleAddToCart(product, 1)}
                        disabled={product.stock === 0}
                      >
                        {product.stock === 0 ? 'Sold Out' : 'Add to Cart'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-products">
            <p>No products found matching your criteria.</p>
            <button
              className="reset-filters-btn"
              onClick={() => {
                setSelectedCategory('all');
                setSearchTerm('');
              }}
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* Shopping Cart Sidebar */}
      <div className={`cart-sidebar ${cartOpen ? 'open' : ''}`}>
        <div className="cart-header">
          <h3>Your Cart</h3>
          <button className="close-cart" onClick={() => setCartOpen(false)}>
            ×
          </button>
        </div>

        {cart.length > 0 ? (
          <>
            <div className="cart-items">
              {cart.map((item) => (
                <div key={item.product.id} className="cart-item">
                  <img
                    src={item.product.imageUrl}
                    alt={item.product.name}
                    className="cart-item-image"
                  />
                  <div className="cart-item-details">
                    <h4>{item.product.name}</h4>
                    <p className="cart-item-price">
                      ${item.product.price.toFixed(2)}
                    </p>
                    <div className="quantity-control">
                      <button
                        onClick={() =>
                          updateQuantity(item.product.id, item.quantity - 1)
                        }
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() =>
                          updateQuantity(item.product.id, item.quantity + 1)
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    className="remove-item"
                    onClick={() => removeFromCart(item.product.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="cart-footer">
              <div className="cart-total">
                <span>Total:</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              <button className="checkout-btn" onClick={navigateToCheckout}>
                Proceed to Checkout
              </button>
            </div>
          </>
        ) : (
          <div className="empty-cart">
            <p>Your cart is empty.</p>
            <p>Add some products to get started!</p>
          </div>
        )}
      </div>

      {/* Product Details Modal */}
      {selectedProduct && (
        <div className="product-modal-overlay" onClick={closeProductDetails}>
          <div className="product-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={closeProductDetails}>
              ×
            </button>

            <div className="modal-content">
              <div className="modal-image">
                <img
                  src={selectedProduct.imageUrl}
                  alt={selectedProduct.name}
                />
              </div>

              <div className="modal-info">
                <h2>{selectedProduct.name}</h2>
                <p className="modal-price">
                  ${selectedProduct.price.toFixed(2)}
                </p>
                <p className="modal-description">
                  {selectedProduct.description}
                </p>

                {selectedProduct.details &&
                  selectedProduct.details.length > 0 && (
                    <div className="product-details">
                      <h3>Product Details</h3>
                      <ul>
                        {selectedProduct.details.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                <div className="stock-info">
                  {selectedProduct.stock
                    ? selectedProduct.stock < 5
                      ? `Only ${selectedProduct.stock} left in stock!`
                      : 'In Stock'
                    : 'Out of Stock'}
                </div>

                <div className="quantity-selector">
                  <label>Quantity:</label>
                  <select defaultValue="1">
                    {[...Array(10)].map((_, i) => (
                      <option key={i} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  className="modal-add-to-cart"
                  onClick={() => {
                    const quantity = parseInt(
                      (
                        document.querySelector(
                          '.quantity-selector select'
                        ) as HTMLSelectElement
                      ).value
                    );
                    handleAddToCart(selectedProduct, quantity);
                  }}
                  disabled={selectedProduct.stock === 0}
                >
                  {selectedProduct.stock === 0 ? 'Sold Out' : 'Add to Cart'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Store;
